import { Injectable } from "@angular/core";
import { Board } from "@common/ADAPT.Common.Model/organisation/board";
import { ExternalDashboard } from "@common/ADAPT.Common.Model/organisation/external-dashboard";
import { MeetingAttendee } from "@common/ADAPT.Common.Model/organisation/meeting-attendee";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { TeamType } from "@common/ADAPT.Common.Model/organisation/team-type";
import { TeamTypeConfiguration } from "@common/ADAPT.Common.Model/organisation/team-type-configuration";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { RxjsBreezeService } from "@common/lib/data/rxjs-breeze.service";
import { INavigationNode } from "@common/route/navigation-node.interface";
import { NavigationNodeBuilder } from "@common/route/navigation-node-builder";
import { NavigationUtilitiesService } from "@common/route/navigation-utilities.service";
import { RouteService } from "@common/route/route.service";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { AuthorisationNotificationService } from "@org-common/lib/authorisation/authorisation-notification.service";
import { JourneyProgressAuthService } from "@org-common/lib/journey-progress/journey-progress-auth.service";
import { journeyProgressPageRoute } from "@org-common/lib/journey-progress/journey-progress-page/journey-progress-page.component";
import { KanbanService } from "@org-common/lib/kanban/kanban.service";
import { teamKanbanPageRoute } from "@org-common/lib/kanban/kanban-page/kanban-page.route";
import { MeetingsService } from "@org-common/lib/meetings/meetings.service";
import { teamMeetingsPageRoute } from "@org-common/lib/meetings/team-meetings-page/team-meetings-page.route";
import { ObjectivesAuthService } from "@org-common/lib/objectives/objectives-auth.service";
import { teamObjectivesPageRoute } from "@org-common/lib/objectives/objectives-page/objectives-page.route";
import { OrganisationService } from "@org-common/lib/organisation/organisation.service";
import { OrganisationAuthService } from "@org-common/lib/organisation/organisation-auth.service";
import { teamCatchupsPageRoute } from "@org-common/lib/peer-catchup/team-catchups-page/team-catchups-page.route";
import { BaseOrgCommonNavigationHierarchyService } from "@org-common/lib/sidebar/base-org-common-navigation-hierarchy.service";
import { analyseTeamAssessmentSurveyPageRoute } from "@org-common/lib/survey/analyse-survey-page/analyse-survey-page.route";
import { manageTeamAssessmentsPageRoute } from "@org-common/lib/survey/team-assessment/manage-team-assessments-page/manage-team-assessments-page.route";
import { TeamAssessmentAuthService } from "@org-common/lib/survey/team-assessment/team-assessment-auth.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import { CommonTeamsAuthService } from "@org-common/lib/teams/common-teams-auth.service";
import { TeamPrivateIconComponent } from "@org-common/lib/teams/team-dashboard-shared/team-private-icon/team-private-icon.component";
import { VisionAuthService } from "@org-common/lib/vision/vision-auth.service";
import { VisionPageRoute } from "@org-common/lib/vision/vision-page/vision-page.component";
import { IntegratedArchitectureFrameworkAuthService } from "app/features/architecture/integrated-architecture/integrated-architecture-framework-auth.service";
import { teamSystemsPageRoute } from "app/features/systemisation/team-systems-page/team-systems-page.component";
import { organisationMapPageRoute } from "app/organisation/organisation-map-page/organisation-map-page.route";
import { teamDashboardPageRoute } from "app/platform/team-dashboard/team-dashboard-page/team-dashboard-page.route";
import { filter, lastValueFrom, merge, Subject, switchMap } from "rxjs";
import { debounceTime, map, tap } from "rxjs/operators";

@Injectable({ providedIn: "root" }) // Ensure this service is provided in the root injector to be injected into owners hierarchy service
export class TeamHierarchyNavigationService extends BaseOrgCommonNavigationHierarchyService {
    public static readonly HierarchyId = "ADAPT.Teams.Navigation.Hierarchy";
    public static readonly IconClass = "fal fa-users";

    public id = TeamHierarchyNavigationService.HierarchyId;

    private teamsRootNode?: INavigationNode;
    private activeTeamNode?: INavigationNode;
    private currentActiveTeam?: INavigationNode;
    private currentProcessingTeamId?: number;
    private _dynamicTeamAdded$ = new Subject<INavigationNode>();
    private _dynamicTeamRemoved$ = new Subject<INavigationNode>();

    // store the count of boards the user has access to
    // so we can refresh the team hierarchy when this number changes
    private boardCount = 0;

    public constructor(
        private navUtilitiesFactory: NavigationUtilitiesService,
        private authService: AuthorisationService,
        private orgService: OrganisationService,
        authNotification: AuthorisationNotificationService,
        rxjsBreezeService: RxjsBreezeService,
        private kanbanService: KanbanService,
        private routeService: RouteService,
        private teamsService: CommonTeamsService,
        meetingsService: MeetingsService,
    ) {
        super(orgService, authNotification);

        this.rebuildHierarchy$ = merge(
            rxjsBreezeService.entityTypeChanged(Team), // still need this for team name change
            rxjsBreezeService.entityTypeChanged(TeamTypeConfiguration), // Team type name changed
            rxjsBreezeService.entityTypeChanged(ExternalDashboard),
            // this will determine if work node will be shown
            rxjsBreezeService.entityTypeChanged(Board).pipe(
                switchMap(() => kanbanService.getAllAccessibleBoards()),
                filter((boards) => this.boardCount !== boards.length),
                tap((boards) => this.boardCount = boards.length),
            ),
            // this will trigger a hierarchy nav refresh if you are the attendee and you don't already has meeting read from global or team permission
            rxjsBreezeService.entityTypeChanged(MeetingAttendee).pipe(
                // this is needed for attendee detach as nav property will be nulled off
                switchMap((meetingAttendee) => meetingsService.getMeetingById(meetingAttendee.meetingId).pipe(
                    map((meeting) => ({ meeting, meetingAttendee })),
                )),
                switchMap(({ meeting, meetingAttendee }) => authService.getHasAccess(CommonTeamsAuthService.ReadTeamMeetings, meeting).pipe(
                    map((hasReadAccess) => ({ hasReadAccess, meetingAttendee })),
                )),
                filter(({ hasReadAccess, meetingAttendee }) => !hasReadAccess && authService.currentPerson?.personId === meetingAttendee.attendeeId),
            ),
        ).pipe(
            debounceTime(100), // only a single rebuild required for multiple boards loaded
        );
    }

    public get dynamicTeamAdded$() {
        return this._dynamicTeamAdded$.asObservable();
    }

    public get dynamicTeamRemoved$() {
        return this._dynamicTeamRemoved$.asObservable();
    }

    public onNodeActivated(activeNode: INavigationNode) {
        if (this.currentActiveTeam) { // this will only be defined for team dynamic node which is not part of 'My Teams'
            let activeNodeInTeamHierarchy = false;
            let nodeIterator: INavigationNode | undefined = activeNode;
            while (nodeIterator != null) {
                if (nodeIterator === this.currentActiveTeam) {
                    activeNodeInTeamHierarchy = true;
                    break;
                }

                nodeIterator = nodeIterator.parent;
            }

            if (!activeNodeInTeamHierarchy) {
                // out of current active team -> will remove
                this.activeTeamNode?.removeChild(this.currentActiveTeam);
                this._dynamicTeamRemoved$.next(this.currentActiveTeam);
                this.currentActiveTeam = undefined;
            }
        }
    }

    public async onDynamicNodeCreated(dynamicNode?: INavigationNode) {
        if (!this.activeTeamNode || !dynamicNode || !this.teamsRootNode) {
            // won't bother doing the rest if no active team node (which is only created for architecture v2)
            return;
        }

        // check if dynamic node has parent of team root node, not doing anything if is not a team node
        let isTeamNode = false;
        let parentNode = dynamicNode.parent;
        while (parentNode) {
            if (parentNode === this.teamsRootNode || parentNode.url === this.teamsRootNode.url) {
                isTeamNode = true;
                break;
            }
            parentNode = parentNode.parent;
        }

        const teamId = this.routeService.currentActivatedRoute?.snapshot.paramMap.get("teamId");
        if (isTeamNode && teamId
            && teamId !== this.currentProcessingTeamId?.toString()) {
            // use currentProcessingTeamId to block double-up of node creation,
            // e.g. team dashboard redirected to team dashboard with query param page=guidance, where each will create a dynamic node.
            // - already prevent that from the nav hierarchy service callback when $locationChangeSuccess
            // - this may still happen if going to a child node of a team from outside and then redirected to another child node before the promises finish
            //   (url different but both under the same team)
            this.currentProcessingTeamId = Number(teamId);
            const teamDashboardUrl = await lastValueFrom(teamDashboardPageRoute.getRoute({ teamId: this.currentProcessingTeamId }));
            if (!this.isTeamDashboardNodeFound(teamDashboardUrl)) {
                const team = await lastValueFrom(this.teamsService.getTeamById(this.currentProcessingTeamId));
                if (team) {
                    // build and add new child
                    this.currentActiveTeam = await this.promiseToBuildTeamNavigationNode(team);

                    // remove all existing children of activeTeamNode
                    const existingChildren = this.activeTeamNode.children.slice();
                    for (const child of existingChildren) {
                        this.activeTeamNode.removeChild(child);
                        this._dynamicTeamRemoved$.next(child);
                    }

                    this.activeTeamNode.addChild(this.currentActiveTeam);
                    this._dynamicTeamAdded$.next(this.currentActiveTeam);
                }
            }

            this.currentProcessingTeamId = undefined;
        }
    }

    private isTeamDashboardNodeFound(teamDashboardUrl: string) {
        if (this.teamsRootNode) {
            return this.teamsRootNode.children.some((node) => matchTeamNode(node, teamDashboardUrl));
        } else {
            return false;
        }

        function matchTeamNode(node: INavigationNode, url: string): boolean {
            if (node.url === url) {
                return true;
            } else if (node.controller !== teamDashboardPageRoute.id) {
                return node.children.some((n) => matchTeamNode(n, url));
            } else { // not search beyond team dashboard node - this node matches teamDashboardPageRoute.id
                return false;
            }
        }
    }

    protected async buildHierarchy() {
        const self = this;

        const teamsRootNodeBuilder = this.navUtilitiesFactory.nodeBuilderForControllerAndParams(organisationMapPageRoute.id)
            .setCustomKeyValue("urlPriority", 100)
            .keepChildrenInAddedOrder()
            .setIconClass(TeamHierarchyNavigationService.IconClass)
            .setHideIconInBreadcrumb(true)
            .promiseToAddChild(promiseToBuildActiveTeamNode())
            .promiseToAddChild(promiseToBuildGroupNode(true))
            .promiseToAddChild(promiseToBuildGroupNode(false));

        this.teamsRootNode = await teamsRootNodeBuilder.promiseToBuild();

        // get this after all the teams have been fetched by the above
        const accessibleBoards = await lastValueFrom(this.kanbanService.getAllAccessibleBoards());
        this.boardCount = accessibleBoards.length;

        return this.teamsRootNode;

        async function promiseToBuildActiveTeamNode() {
            self.activeTeamNode = await self.navUtilitiesFactory.nodeBuilder()
                .setTitle("Current Group")
                .setCustomKeyValue("expandOnLoad", true)
                .setCustomKeyValue("isHiddenInBreadcrumbs", true)
                .setCustomKeyValue("displayAsAccordionInHierarchy", true)
                .promiseToBuild();
            return self.activeTeamNode;
        }

        async function promiseToBuildGroupNode(personal = true) {
            const activePersonTeamIds = (await self.teamsService.promiseToGetActiveTeamsForCurrentPerson())
                .map((team) => team.teamId);

            const groupNodeBuilder = self.navUtilitiesFactory.nodeBuilder()
                .setTitle(personal ? "My Groups" : "Other Groups")
                // expand for stakeholdermanagers by default
                .setCustomKeyValue("expandOnLoad", personal || activePersonTeamIds.length === 0)
                .setCustomKeyValue("isHiddenInBreadcrumbs", true)
                .setCustomKeyValue("displayAsAccordionInHierarchy", true)
                .keepChildrenInAddedOrder();

            const teamTypes = await self.teamsService.promiseToGetTeamTypeConfigurations();
            for (const teamType of teamTypes) {
                groupNodeBuilder.promiseToAddChild(promiseToBuildTeamTypeNode(teamType, activePersonTeamIds, personal));
            }

            return groupNodeBuilder.promiseToBuild();
        }

        async function promiseToBuildTeamTypeNode(teamTypeConfiguration: TeamTypeConfiguration, activePersonTeamIds: number[], personal = true) {
            const allActiveTeams = await self.teamsService.promiseToGetAllActiveTeams();

            const myTeamsNodeBuilder = self.navUtilitiesFactory.nodeBuilder()
                .setTitle(personal ? `My ${teamTypeConfiguration.name}s` : `Other ${teamTypeConfiguration.name}s`)
                // expand for stakeholdermanagers by default
                .setCustomKeyValue("expandOnLoad", personal || activePersonTeamIds.length === 0)
                .setCustomKeyValue("isHiddenInBreadcrumbs", true)
                .setCustomKeyValue("displayAsAccordionInHierarchy", true);

            allActiveTeams.filter((team) => team.teamTypeConfiguration === teamTypeConfiguration)
                .filter((team) => personal
                    ? activePersonTeamIds.includes(team.teamId)
                    : !activePersonTeamIds.includes(team.teamId))
                .map((team) => self.promiseToBuildTeamNavigationNode(team))
                .forEach(myTeamsNodeBuilder.promiseToAddChild);
            return myTeamsNodeBuilder.promiseToBuild();
        }
    }

    @Autobind
    public async promiseToBuildTeamNavigationNode(team: Team, useBuilder?: NavigationNodeBuilder) {
        const teamParams = {
            teamId: team.teamId,
        };

        const builder = useBuilder ??
            this.navUtilitiesFactory.nodeBuilderForControllerAndParams(teamDashboardPageRoute.id, teamParams)
                .setTitle(team.name)
                .keepChildrenInAddedOrder()
                .setCustomKeyValue("exclusiveExpand", true);

        const hasExternalDashboardAccess = await this.authService.promiseToGetHasAccess(OrganisationAuthService.ViewExternalDashboard, team);
        if (hasExternalDashboardAccess) {
            const externalDashboards = (await this.orgService.promiseToGetExternalDashboards(team.teamId)).map(
                (externalDashboard) => this.promiseToBuildExternalDashboardNode(externalDashboard, this.navUtilitiesFactory, team));
            builder.promiseToAddChildren(externalDashboards);
        }

        const promiseToBuildTeamSystemsNode = this.navUtilitiesFactory
            .nodeBuilderForControllerAndParams(teamSystemsPageRoute.id, teamParams)
            .ifAuthorised(IntegratedArchitectureFrameworkAuthService.ReadSystem, team)
            .promiseToBuild();
        const promiseToBuildTeamObjectivesNode = this.navUtilitiesFactory
            .nodeBuilderForControllerAndParams(teamObjectivesPageRoute.id, teamParams)
            .ifAuthorised(ObjectivesAuthService.ReadObjectivesForTeam, team)
            .promiseToBuild();
        const promiseToBuildTeamWorkNode = this.navUtilitiesFactory
            .nodeBuilderForControllerAndParams(teamKanbanPageRoute.id, teamParams)
            .ifAuthorised(CommonTeamsAuthService.ViewTeamKanban, team)
            .promiseToBuild();
        const promiseToBuildTeamMeetingsNode = this.navUtilitiesFactory
            .nodeBuilderForControllerAndParams(teamMeetingsPageRoute.id, teamParams)
            .ifAuthorised(CommonTeamsAuthService.ViewTeamMeetings, team)
            .promiseToBuild();
        const promiseToBuildTeamVisionNode = this.navUtilitiesFactory
            .nodeBuilderForControllerAndParams(VisionPageRoute.id, teamParams)
            .ifAuthorised(VisionAuthService.ReadVision, team)
            .promiseToBuild();

        const promiseToBuildTeamNetworkHealthNode = this.navUtilitiesFactory
            .nodeBuilderForControllerAndParams(teamCatchupsPageRoute.id, teamParams)
            .ifAuthorised(CommonTeamsAuthService.ViewTeamNetworkHealth, team)
            .promiseToBuild();

        const promiseToBuildAnalyseTeamAssessmentNode = this.navUtilitiesFactory
            .nodeBuilderForControllerAndParams(analyseTeamAssessmentSurveyPageRoute.id, teamParams)
            .ifAuthorised(TeamAssessmentAuthService.ReadTeamAssessment, team)
            .promiseToBuild();

        const promiseToBuildTeamAssessmentNode = this.navUtilitiesFactory
            .nodeBuilderForControllerAndParams(manageTeamAssessmentsPageRoute.id, teamParams)
            .ifAuthorised(TeamAssessmentAuthService.ReadTeamAssessment, team)
            .promiseToAddChild(promiseToBuildAnalyseTeamAssessmentNode)
            .promiseToBuild();

        builder.promiseToAddChild(promiseToBuildTeamSystemsNode)
            .promiseToAddChild(promiseToBuildTeamVisionNode)
            .promiseToAddChild(promiseToBuildTeamObjectivesNode)
            .promiseToAddChild(promiseToBuildTeamWorkNode)
            .promiseToAddChild(promiseToBuildTeamMeetingsNode)
            .promiseToAddChild(promiseToBuildTeamNetworkHealthNode)
            .promiseToAddChild(promiseToBuildTeamAssessmentNode);

        if (team.teamType === TeamType.Owner || team.teamType === TeamType.Leadership) {
            const promiseToBuildProgressDashboardNode = this.navUtilitiesFactory.nodeBuilderForControllerAndParams(journeyProgressPageRoute.id)
                .ifAuthorised(JourneyProgressAuthService.ReadJourneyProgress)
                .promiseToBuild();
            builder.promiseToAddChild(promiseToBuildProgressDashboardNode);
        }

        if (team.isPrivate && !useBuilder) { // only set this if not using a custom builder (e.g. from owners hierarchy service)
            builder.setIconClass(TeamPrivateIconComponent.IconClass)
                .setIconPositionRight(true);
        }

        return builder.promiseToBuild();
    }
}
